/**
 * The dashboard helpdesk card.
 *
 * MKS-339 made it three SLA numbers over the viewer's own intake-enabled
 * projects, and hid it entirely from anyone who had none. MKS-410 makes the
 * helpdesk everyone's, so the card now says something to everyone — but not
 * the same thing, because the two audiences have opposite questions:
 *
 *   - an ADMIN asks "what is waiting for me to triage?" → the queue's counts,
 *     each linking into `/helpdesk` filtered to what it counted;
 *   - EVERYONE ELSE asks "what happened to the things I filed?" → their own
 *     "My requests" counts, linking to the page that lists them.
 *
 * Counts are derived from the lists themselves rather than a summary
 * endpoint, and deliberately from the SAME query keys those two pages use
 * (`['helpdeskRequests','queue',{}]` and `['helpdeskRequests','mine']`), so
 * opening either page after the dashboard is a cache hit and the number can
 * never disagree with the list behind it.
 *
 * Keeps the dark hero-variant styling of the card it replaces (Plan 41-16,
 * D-Dashboard-layout-v3) so the hero row's two-up composition is unchanged.
 */
import { useNavigate } from 'react-router-dom';
import { useFlag } from '../../services/flagsContext';
import { useAuth } from '../../services/authContext';
import {
  useAdminHelpdeskRequests,
  useMyHelpdeskRequests,
} from '../../hooks/useHelpdeskRequests';
import type { HelpdeskRequestRecord } from '../../services/api/helpdeskRequests';

/**
 * The flag-off branch: byte-for-byte the card this one replaces (Phase 41
 * Plan 41-05 / 41-16), so production keeps exactly today's hero row until
 * MKS-339 / MKS-410 are verified.
 */
function HelpdeskComingSoonCard() {
  return (
    <div
      data-testid="helpdesk-card"
      className="bg-gray-900 text-white rounded-lg border border-gray-800 p-4 flex flex-col"
    >
      <div className="text-[10.5px] uppercase tracking-wider text-gray-400 mb-1">
        Coming soon
      </div>
      <div className="text-[15px] font-semibold text-white mb-2">Helpdesk</div>
      <p className="text-[12.5px] text-gray-300 mb-4 flex-1">
        Tickets, requests &amp; FAQ — soon.
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          disabled
          className="text-[11.5px] px-2.5 py-1.5 rounded-md font-medium bg-white/10 text-gray-300 border border-white/15 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          Submit ticket
        </button>
        <button
          type="button"
          disabled
          className="text-[11.5px] px-2.5 py-1.5 rounded-md font-medium text-gray-300 hover:bg-white/5 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          FAQ
        </button>
      </div>
    </div>
  );
}

interface StatProps {
  label: string;
  value: number;
  onClick: () => void;
  /** Reads in warning colour once it is non-zero. */
  alarm?: boolean;
}

function Stat({ label, value, onClick, alarm }: StatProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex-1 text-left rounded-md px-2 py-1.5 hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-white/20"
    >
      <div
        className={`text-[22px] font-semibold leading-none ${
          alarm && value > 0 ? 'text-amber-300' : 'text-white'
        }`}
      >
        {value}
      </div>
      <div className="text-[10.5px] uppercase tracking-wider text-gray-400 mt-1">{label}</div>
    </button>
  );
}

function CardShell({
  action,
  children,
}: {
  action: { label: string; onClick: () => void };
  children: React.ReactNode;
}) {
  return (
    <div
      data-testid="helpdesk-card"
      className="bg-gray-900 text-white rounded-lg border border-gray-800 p-4 flex flex-col"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-[15px] font-semibold text-white">Helpdesk</div>
        <button
          type="button"
          onClick={action.onClick}
          className="text-[11.5px] px-2.5 py-1.5 rounded-md font-medium bg-white/10 text-gray-200 border border-white/15 hover:bg-white/15"
        >
          {action.label}
        </button>
      </div>
      <div className="flex gap-2 flex-1 items-center">{children}</div>
    </div>
  );
}

/** Resolved within the current calendar month, in the viewer's own timezone. */
function resolvedThisMonth(requests: HelpdeskRequestRecord[]): number {
  const now = new Date();
  return requests.filter((r) => {
    if (r.status !== 'resolved' || !r.resolvedAt) return false;
    const at = new Date(r.resolvedAt);
    return (
      at.getFullYear() === now.getFullYear() && at.getMonth() === now.getMonth()
    );
  }).length;
}

export function HelpdeskCard() {
  const navigate = useNavigate();
  const helpdeskOn = useFlag('mks_339_helpdesk_inbox');
  const { isAdmin } = useAuth();

  // Both queries are disabled while the flag is off — the backend routes
  // answer 404 there, and an error-state query behind a placeholder card is
  // noise in Sentry for no gain — and only the branch that renders runs.
  const queue = useAdminHelpdeskRequests({}, helpdeskOn && isAdmin);
  const mine = useMyHelpdeskRequests(helpdeskOn && !isAdmin);

  if (!helpdeskOn) return <HelpdeskComingSoonCard />;

  const active = isAdmin ? queue : mine;
  // Nothing at all until the counts are known — a card that flashes zeroes and
  // then fills in reads as "you have nothing", which is a worse lie than a
  // moment of empty space.
  if (active.isLoading || !active.data) return null;

  if (isAdmin) {
    const rows = queue.data ?? [];
    const newCount = rows.filter((r) => r.status === 'new').length;
    const inProgress = rows.filter((r) => r.status === 'in_progress').length;
    return (
      <CardShell action={{ label: 'Open queue', onClick: () => navigate('/helpdesk') }}>
        <Stat
          label="New"
          value={newCount}
          alarm
          onClick={() => navigate('/helpdesk?status=new')}
        />
        <Stat
          label="In progress"
          value={inProgress}
          onClick={() => navigate('/helpdesk?status=in_progress')}
        />
        {/* The number is this month's; the list behind it is every resolved
            request, because the queue has no date filter to narrow it with.
            The label says which of the two it is counting. */}
        <Stat
          label="Resolved this month"
          value={resolvedThisMonth(rows)}
          onClick={() => navigate('/helpdesk?status=resolved')}
        />
      </CardShell>
    );
  }

  const rows = mine.data ?? [];
  const open = rows.filter(
    (r) => r.status !== 'resolved' && r.status !== 'rejected',
  ).length;
  const resolved = rows.filter((r) => r.status === 'resolved').length;
  const rejected = rows.filter((r) => r.status === 'rejected').length;

  // Every tile goes to the same page: "My requests" is one list, not a
  // filterable queue, so a per-tile filter would be a link to a view that does
  // not exist.
  const openMine = () => navigate('/report-issue');

  return (
    <CardShell action={{ label: 'New request', onClick: openMine }}>
      <Stat label="Open" value={open} onClick={openMine} />
      <Stat label="Resolved" value={resolved} onClick={openMine} />
      <Stat label="Rejected" value={rejected} onClick={openMine} />
    </CardShell>
  );
}
